import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { PageHeader } from '@/components/common/PageHeader';
import { ROUTES } from '@/lib/constants/routes';
import { getApiErrorCode } from '@/lib/utils/error.utils';
import { PartnerProfileView } from '@/features/trusted-partners/components/PartnerProfileView';
import { usePartnerDetail } from '@/features/trusted-partners/hooks/useTrustedPartners';
import {
  PARTNER_NOT_AVAILABLE_CODE,
  TRUSTED_PARTNERS_STRINGS as S,
} from '@/features/trusted-partners/trusted-partners.constants';

/**
 * A partner's profile page (FR-111, `/trusted-partners/:entityId`), open to every
 * logged-in persona. A partner suspended or deleted since the list was loaded
 * answers with a dedicated code and renders "no longer available" (Card 11)
 * rather than a generic failure.
 */
export function TrustedPartnerProfilePage() {
  const { entityId = '' } = useParams<{ entityId: string }>();
  const query = usePartnerDetail(entityId);
  const isUnavailable = query.isError && getApiErrorCode(query.error) === PARTNER_NOT_AVAILABLE_CODE;

  return (
    <div className="min-h-full bg-page-bg">
      <PageHeader
        title={query.data?.businessName ?? S.PAGE_TITLE}
        breadcrumb={[{ label: S.BACK_TO_PAGE, to: ROUTES.TRUSTED_PARTNERS }, { label: query.data?.businessName ?? S.PROFILE_BREADCRUMB_FALLBACK }]}
      />
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8">
        {query.isPending && (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        )}
        {isUnavailable && (
          <EmptyState
            title={S.PROFILE_UNAVAILABLE_TITLE}
            description={S.PROFILE_UNAVAILABLE_DESCRIPTION}
            action={
              <Button asChild variant="outline" className="px-6! py-2! text-sm">
                <Link to={ROUTES.TRUSTED_PARTNERS}>{S.BACK_TO_PAGE}</Link>
              </Button>
            }
          />
        )}
        {query.isError && !isUnavailable && (
          <ErrorState description={S.PROFILE_LOAD_FAILED} onRetry={() => void query.refetch()} />
        )}
        {query.isSuccess && <PartnerProfileView partner={query.data} />}
      </div>
    </div>
  );
}
